/* Tidy Trim builder. Shopify is authoritative for cart prices, stock and discounts. */
(() => {
  'use strict';

  const clean = value => String(value ?? '').trim();
  const moneyValue = value => Number.isFinite(value) && value >= 0 ? Math.round(value) : null;
  const optionLabel = values => (values || []).map(clean).filter(value => value && value !== 'Default Title').join(' / ');
  function quantity(value, rule = {}) {
    const min = Math.max(1, Number(rule.min) || 1);
    const step = Math.max(1, Number(rule.increment) || 1);
    const max = Number(rule.max) > 0 ? Number(rule.max) : Infinity;
    if (max < min) return 0;
    const requested = Number.isFinite(Number(value)) ? Number(value) : min;
    return Math.min(min + Math.floor((Math.max(min, requested) - min) / step) * step,
      Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity);
  }
  function calculate(config, selections) {
    const offerSupported = config.offerMode !== 'disabled' && config.currency === 'USD' && !config.wholesale;
    const eligibleSkus = new Set((config.rearSkus || []).map(clean).filter(Boolean));
    const eligibleVariants = new Set((config.rearVariantIds || []).map(clean).filter(Boolean));
    const rows = config.parts.filter(p => selections[p.key]?.selected).map(part => {
      const state = selections[part.key];
      const variant = part.product?.variants.find(v => String(v.id) === String(state.variantId));
      const connected = !!part.product;
      const invalidMapping = connected && (config.forbiddenProductIds || []).filter(Boolean).some(id => String(id) === String(part.product.id));
      const price = connected ? moneyValue(variant?.price) : !config.allowCart && config.currency === 'USD' ? moneyValue(part.draftPrice) : null;
      const qty = quantity(state.quantity, variant);
      return { part, variant, quantity: qty, price, total: price === null ? null : price * qty,
        discount: 0, pending: invalidMapping || !connected || !variant || !clean(variant.sku), unavailable: invalidMapping || connected && (!variant?.available || !qty),
        option: connected ? variant ? optionLabel(variant.options) : 'Configuration unavailable' : clean(state.draftOption) };
    });
    const front = rows.reduce((sum, row) => sum + (row.part.qualifies && !row.unavailable ? row.total || 0 : 0), 0);
    const threshold = Math.max(1, moneyValue(config.threshold) || 72500);
    const unlocked = offerSupported && front >= threshold;
    let remainingRearUnits = 1;
    for (const row of rows) {
      if (unlocked && remainingRearUnits && row.part.rearOffer && !row.unavailable && row.quantity > 0 &&
          (eligibleVariants.size ? eligibleVariants.has(clean(row.variant?.id)) : eligibleSkus.has(clean(row.variant?.sku))) && row.price !== null) {
        row.discount = moneyValue(config.rearDiscount) !== null ? Math.min(row.price, config.rearDiscount)
          : moneyValue(config.rearPrice) !== null ? Math.max(0, row.price - config.rearPrice) : 0;
        if (row.discount) remainingRearUnits -= 1;
      }
    }
    const subtotal = rows.reduce((sum, row) => sum + (row.total || 0), 0);
    const saving = rows.reduce((sum, row) => sum + row.discount, 0);
    return { rows, front, threshold, unlocked, offerSupported, subtotal, saving, total: subtotal - saving,
      incomplete: rows.some(row => row.price === null), pending: rows.some(row => row.pending),
      units: rows.reduce((sum, row) => sum + row.quantity, 0) };
  }
  function kitAdvice(config, model) {
    const core = ['driver_void', 'passenger_void', 'slider_upper', 'pillar_upper', 'pillar_lower', 'pillar_middle'];
    if (config.wholesale || config.currency !== 'USD' || !core.every(key => model.rows.some(r => r.part.key === key))) return null;
    const front = config.kits?.front;
    const bundle = config.kits?.bundle;
    if (!front?.url && !bundle?.url) return null;
    const windowRow = model.rows.find(r => r.part.key === 'window');
    const windowKinds = { '42590331895999': 'base', '42590331928767': 'steel', '45338405470399': 'segmented' };
    const windowKind = windowRow ? windowKinds[String(windowRow.variant?.id)] : 'none';
    const configKind = option => /RHD/i.test(option) ? 'rhd' : /No Window/i.test(option) ? 'none' : /Steel Inserts/i.test(option) ? 'steel' : /Segmented/i.test(option) ? 'segmented' : /Window Trim Included/i.test(option) ? 'base' : null;
    const slider = model.rows.find(r => r.part.key === 'slider_upper');
    const sliderOption = clean(slider?.variant?.options?.[0]);
    const available = product => (product?.variants || []).filter(v => v.available && moneyValue(v.price) !== null);
    const match = available(front).find(v => windowKind && configKind(v.options?.[0]) === windowKind &&
      /Standard RAW/.test(v.options?.[1]) && clean(v.options?.[2]) === sliderOption);
    const minimum = product => { const prices = available(product).map(v => v.price); return prices.length ? Math.min(...prices) : null; };
    const rear = model.rows.find(r => r.part.rearOffer);
    const frontRows = model.rows.filter(r => core.includes(r.part.key) || r.part.key === 'window');
    const singleSet = frontRows.every(r => r.quantity === 1 && !r.pending && !r.unavailable && r.price !== null);
    const frontTotal = frontRows.reduce((sum, r) => sum + (r.total || 0), 0);
    return { rear: !!rear, frontUrl: front?.url, bundleUrl: rear ? bundle?.url : null,
      frontVariant: match?.id, frontPrice: match?.price ?? minimum(front), exact: !!match,
      bundlePrice: minimum(bundle),
      // Replacing qualifying pieces can remove the rear discount. Never claim front-only savings on a rear build.
      saving: !rear && singleSet && match ? Math.max(0, frontTotal - match.price) : 0 };
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { quantity, calculate, kitAdvice };
    return;
  }
  if (!window.customElements || customElements.get('tidy-trim-builder')) return;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const button = (className, text, action, key) => {
    const node = el('button', className, text);
    node.type = 'button';
    node.dataset.action = action;
    if (key) node.dataset.key = key;
    node.dataset.focus = `${action}:${key || ''}`;
    return node;
  };
  const safeImage = value => {
    try { const url = new URL(value, window.location.origin); return /^https?:$/.test(url.protocol) ? url.href : ''; }
    catch (_) { return ''; }
  };

  class TidyTrimBuilder extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true;
      this.events = new AbortController();
      const signal = this.events.signal;
      try {
        this.config = JSON.parse(this.querySelector('[data-ttb-config]').textContent);
        this.config.views = this.config.views.filter((v, i, all) => clean(v.key) && all.findIndex(x => x.key === v.key) === i);
        this.config.parts = this.config.parts.filter(p => this.config.views.some(v => v.key === p.view));
        if (new Set(this.config.parts.map(p => p.key)).size !== this.config.parts.length) throw new Error('Duplicate piece keys');
        if (!this.config.views.length) throw new Error('No views configured');
        this.selections = {};
        this.cartBusy = false;
        this.journalKey = this.config.storageKey + '-cart-attempt';
        try { this.attempt = JSON.parse(sessionStorage.getItem(this.journalKey) || 'null'); } catch (_) { this.attempt = null; }
        this.transport = new window.TidyTrimCart.CartTransport({ fetcher: window.fetch.bind(window), root: this.config.root || '/', save: attempt => { sessionStorage.setItem(this.journalKey, JSON.stringify(attempt)); this.attempt = attempt; } });
        let saved = {};
        try { saved = JSON.parse(sessionStorage.getItem(this.config.storageKey) || '{}'); } catch (_) { /* Storage is optional. */ }
        for (const part of this.config.parts) {
          part.draftOptions = (part.draftOptions || []).map(clean).filter(Boolean);
          const variants = part.product?.variants || [];
          const existing = variants.find(v => String(v.id) === String(saved.parts?.[part.key]?.variantId));
          const variant = existing || variants.find(v => v.available) || variants[0];
          const prior = saved.parts?.[part.key] || {};
          this.selections[part.key] = { selected: prior.selected === true, variantId: variant?.id,
            quantity: quantity(prior.quantity || 1, variant),
            draftOption: part.draftOptions.includes(prior.draftOption) ? prior.draftOption : part.draftOptions[0] || 'Standard RAW' };
        }
        this.view = this.config.views.some(v => v.key === saved.view) ? saved.view : this.config.views[0].key;
        this.applyProductLink(window.location.search);
        document.addEventListener('cart:cleared', () => this.refreshCartState(), { signal });
        this.addEventListener('click', e => this.onClick(e), { signal });
        this.addEventListener('change', e => this.onChange(e), { signal });
        this.addEventListener('pointerover', e => this.highlightFromEvent(e), { signal });
        this.addEventListener('pointerout', e => {
          const next = e.relatedTarget instanceof Element ? e.relatedTarget.closest('[data-highlight-key]') : null;
          if (!next || !this.contains(next)) this.paintHighlight(null);
        }, { signal });
        this.addEventListener('focusin', e => this.highlightFromEvent(e), { signal });
        this.addEventListener('focusout', e => {
          const next = e.relatedTarget instanceof Element ? e.relatedTarget.closest('[data-highlight-key]') : null;
          if (!next || !this.contains(next)) this.paintHighlight(null);
        }, { signal });
        this.addEventListener('keydown', e => {
          if (e.target.matches('.ttb__shape') && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault(); this.toggle(e.target.dataset.key);
          }
        }, { signal });
        window.addEventListener('pageshow', () => { this.cartRun = (this.cartRun || 0) + 1; this.cartBusy = false; this.render(); this.refreshCartState(); }, { signal });
        this.querySelector('[data-ttb-dialog]').addEventListener('close', () => this.querySelector('[data-ttb-review]').focus(), { signal });
        this.querySelector('[data-ttb-app]').hidden = false;
        this.querySelector('[data-ttb-fallback]').hidden = true;
        this.render();
      } catch (error) {
        this.querySelector('[data-ttb-fallback]').hidden = false;
        this.querySelector('[data-ttb-app]').hidden = true;
        console.warn('Tidy Trim preview could not initialize:', error.message);
      }
    }
    disconnectedCallback() { this.events?.abort(); this.active = false; }
    applyProductLink(search) {
      const params = new URLSearchParams(search);
      const linkedKeys = ['driver_void', 'passenger_void', 'slider_upper', 'pillar_upper', 'pillar_lower', 'pillar_middle'];
      const key = params.get('part');
      if (!linkedKeys.includes(key)) return;
      const part = this.config.parts.find(p => p.key === key);
      const state = this.selections[key];
      if (!part || !state) return;
      const variant = part.product?.variants.find(v => String(v.id) === params.get('variant'));
      state.selected = true;
      if (variant) {
        state.variantId = variant.id;
        state.quantity = quantity(state.quantity, variant);
      }
      this.view = part.view;
    }
    money(cents) {
      if (cents === null) return 'Price pending';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.config.currency }).format(cents / 100);
    }
    node(name) { return this.querySelector(`[data-ttb-${name}]`); }
    save() {
      try { sessionStorage.setItem(this.config.storageKey, JSON.stringify({ version: 1, view: this.view, parts: this.selections })); }
      catch (_) { /* The preview remains fully usable without storage. */ }
    }
    announce(message) { this.node('status').textContent = message; }
    highlightFromEvent(event) {
      // Touch users select directly; no sticky hover state is needed.
      if (event.pointerType === 'touch') return;
      const target = event.target.closest('[data-highlight-key]');
      if (target && this.contains(target)) this.paintHighlight(target.dataset.highlightKey);
    }
    paintHighlight(key) {
      const part = this.config.parts.find(p => p.key === key && p.view === this.view);
      this.highlightedKey = part?.key || null;
      for (const node of this.querySelectorAll('[data-highlight-key]')) node.classList.toggle('is-hovered', !!part && node.dataset.highlightKey === part.key);
      const detail = this.node('hover-detail');
      detail.replaceChildren();
      if (!part) {
        detail.append(el('strong', '', 'Explore the shapes'), el('span', '', 'Hover to identify. Click or tap to select.'));
        return;
      }
      const state = this.selections[part.key];
      const variant = part.product?.variants.find(v => String(v.id) === String(state.variantId));
      const price = part.product ? moneyValue(variant?.price) : !this.config.allowCart && this.config.currency === 'USD' ? moneyValue(part.draftPrice) : null;
      detail.append(el('strong', '', `${state.selected ? '✓ ' : ''}${part.title}`),
        el('span', '', `${this.money(price)}${part.product ? '' : ' planned'} · ${state.selected ? 'Selected. Click to remove.' : 'Click to select.'}`));
    }
    shapeOverlay(parts) {
      const map = window.TidyTrimHighlightShapes;
      const ns = 'http://www.w3.org/2000/svg';
      const svgNode = (name, attrs) => {
        const node = document.createElementNS(ns, name);
        for (const [key,value] of Object.entries(attrs || {})) node.setAttribute(key, value);
        return node;
      };
      const overlay = svgNode('svg', {class:'ttb__shape-overlay',viewBox:`0 0 ${map.width} ${map.height}`,role:'group','aria-label':'Select front trim pieces by their shape'});
      const defs = svgNode('defs');
      const maskId = `ttb-window-visible-${this.config.sectionId}`;
      const mask = svgNode('mask', {id:maskId,maskUnits:'userSpaceOnUse',x:0,y:0,width:map.width,height:map.height});
      mask.append(svgNode('rect', {width:map.width,height:map.height,fill:'white'}));
      // Clear the foreground silhouette and its anti-aliased edge from the window highlight.
      for (const key of ['driver_void','passenger_void']) mask.append(svgNode('path', {d:map.parts[key],fill:'black',stroke:'black','stroke-width':6,'stroke-linejoin':'round'}));
      defs.append(mask); overlay.append(defs);
      for (const part of parts) {
        const d = map.parts[part.key];
        if (!d) continue;
        const shape = svgNode('path', {class:'ttb__shape',d,'fill-rule':'evenodd',role:'button',tabindex:0,
          'aria-label':part.title,'aria-pressed':String(this.selections[part.key].selected),
          'data-action':'toggle','data-key':part.key,'data-highlight-key':part.key,'data-focus':`shape:${part.key}`});
        if (part.key === 'window') shape.setAttribute('mask', `url(#${maskId})`);
        overlay.append(shape);
      }
      return overlay;
    }
    toggle(key) {
      const state = this.selections[key];
      if (!state || this.cartBusy) return;
      state.selected = !state.selected;
      this.render();
      this.announce(`${this.config.parts.find(p => p.key === key).title} ${state.selected ? 'selected' : 'removed'}. ${this.model.rows.length} selections in your build.`);
    }
    onClick(event) {
      const target = event.target.closest('button, .ttb__shape');
      if (!target || !this.contains(target)) return;
      if (target.hasAttribute('data-ttb-new-build') && !this.cartBusy && !this.cartChecking && this.attempt?.state === 'complete') {
        sessionStorage.removeItem(this.journalKey); this.attempt = null;
        this.cartNotice = '';
        for (const state of Object.values(this.selections)) state.selected = false;
        this.node('dialog').close(); this.render(); this.announce('New build started. Your previous pieces remain in the cart.'); return;
      }
      if (target.hasAttribute('data-ttb-add')) { this.addToCart(); return; }
      if (this.cartBusy && !target.hasAttribute('data-ttb-close')) return;
      const { action, key } = target.dataset;
      if (action === 'toggle') this.toggle(key);
      if (action === 'view') { this.view = key; this.render(); }
      if (action === 'remove') { this.selections[key].selected = false; this.render(); this.announce('Selection removed. Your preview has been updated.'); }
      if (action === 'minus' || action === 'plus') {
        const state = this.selections[key];
        const part = this.config.parts.find(p => p.key === key);
        const variant = part.product?.variants.find(v => String(v.id) === String(state.variantId));
        state.quantity = quantity(state.quantity + (action === 'plus' ? 1 : -1) * (Number(variant?.increment) || 1), variant);
        this.render();
      }
      if (target.hasAttribute('data-ttb-clear')) {
        for (const part of this.config.parts) {
          const state = this.selections[part.key];
          const variants = part.product?.variants || [];
          const variant = variants.find(v => v.available) || variants[0];
          Object.assign(state, {selected:false, variantId:variant?.id, quantity:quantity(1,variant), draftOption:part.draftOptions[0] || 'Standard RAW'});
        }
        this.render(); this.announce('Your build has been cleared.');
      }
      if (target.hasAttribute('data-ttb-review')) this.review();
      if (target.hasAttribute('data-ttb-close')) this.node('dialog').close();
    }
    onChange(event) {
      const target = event.target;
      if (this.cartBusy) return;
      if (!target.matches('[data-part-control]')) return;
      const part = this.config.parts.find(p => p.key === target.dataset.key);
      if (!part) return;
      const state = this.selections[part.key];
      if (target.dataset.partControl === 'variant') {
        const chosen = part.product.variants.find(v => String(v.id) === target.value);
        if (!chosen) return;
        state.variantId = chosen.id;
        state.quantity = quantity(state.quantity, chosen);
      } else if (target.dataset.partControl === 'draft') {
        if (part.draftOptions.includes(target.value)) state.draftOption = target.value;
      } else if (target.dataset.partControl === 'quantity') {
        const variant = part.product?.variants.find(v => String(v.id) === String(state.variantId));
        state.quantity = quantity(target.value, variant);
      }
      this.render(); this.announce('Your configuration and preview totals have been updated.');
    }
    render() {
      const focus = this.contains(document.activeElement) ? document.activeElement.dataset.focus : null;
      this.model = calculate(this.config, this.selections);
      const view = this.config.views.find(v => v.key === this.view);
      const parts = this.config.parts.filter(p => p.view === this.view);
      const tabs = this.node('views'); tabs.replaceChildren();
      for (const area of this.config.views) {
        const count = this.config.parts.filter(p => p.view === area.key && this.selections[p.key].selected).length;
        const tab = button('ttb__view', `${area.title}${count ? ` (${count})` : ''}`, 'view', area.key);
        tab.setAttribute('aria-pressed', String(area.key === this.view)); tabs.append(tab);
      }
      const diagram = this.node('diagram'); diagram.replaceChildren();
      if (view.image && safeImage(view.image)) {
        const stage = el('div', 'ttb__image-stage');
        const photo = el('img', 'ttb__reference'); photo.src = safeImage(view.image); photo.alt = view.alt || view.title;
        photo.width = Number(view.width) || 4032; photo.height = Number(view.height) || 3024; photo.decoding = 'async'; photo.draggable = false;
        stage.append(photo);
        const hasShapes = view.key === 'front' && !!window.TidyTrimHighlightShapes;
        if (view.markers && hasShapes) stage.append(this.shapeOverlay(parts));
        if (view.markers && !hasShapes) for (const part of parts) {
          const marker = button('ttb__marker', part.number, 'toggle', part.key);
          marker.dataset.focus = `marker:${part.key}`;
          marker.style.left = `${Math.max(0, Math.min(100, Number(part.x) || 0))}%`;
          marker.style.top = `${Math.max(0, Math.min(100, Number(part.y) || 0))}%`;
          marker.setAttribute('aria-label', `${part.number}: ${part.title}`);
          marker.setAttribute('aria-pressed', String(this.selections[part.key].selected)); stage.append(marker);
        }
        diagram.append(stage);
      } else diagram.append(el('p', 'ttb__photo-empty', 'Reference photo to come. You can still choose every part from the list.'));
      this.node('caption').textContent = view.caption || '';
      this.querySelector('.ttb__legend').hidden = !view.markers;
      const visualNote = this.querySelector('.ttb__visual-note p');
      visualNote.replaceChildren(el('strong', '', view.markers ? 'Point to the piece you need.' : 'One complete rear assembly.'),
        document.createTextNode(view.markers ? 'Hover over a shape to identify it. Click to select; tap on mobile. Selected pieces stay orange.' : 'Choose the kit, then select the exact fit and finish. Rear pieces are not sold individually here.'));
      this.node('hover-detail').hidden = !view.markers;
      this.querySelector('.ttb__parts-note').textContent = view.markers
        ? 'Select a shape or choose its name here. Both stay in sync. Choose the configuration that fits your van.'
        : 'Choose the complete rear kit and its configuration. Rear pieces are not sold individually here.';
      this.node('view-title').textContent = view.title;
      this.node('selected-count').textContent = `${parts.filter(p => this.selections[p.key].selected).length} / ${parts.length} selected`;
      const list = this.node('parts'); list.replaceChildren();
      for (const part of parts) list.append(this.partCard(part));
      this.renderSummary();
      this.paintHighlight(this.highlightedKey);
      this.node('review').disabled = !this.model.rows.length;
      this.node('clear').disabled = !this.model.rows.length;
      this.updateCartControls();
      if (focus) {
        const replacement = [...this.querySelectorAll('[data-focus]')].find(n => n.dataset.focus === focus);
        replacement?.focus({ preventScroll: true });
      }
      this.save();
    }
    partCard(part) {
      const state = this.selections[part.key];
      const variant = part.product?.variants.find(v => String(v.id) === String(state.variantId));
      const price = part.product ? moneyValue(variant?.price) : !this.config.allowCart && this.config.currency === 'USD' ? moneyValue(part.draftPrice) : null;
      const card = el('div', `ttb__part${state.selected ? ' is-selected' : ''}`);
      card.dataset.highlightKey = part.key;
      const choose = button('ttb__part-toggle', undefined, 'toggle', part.key);
      choose.setAttribute('aria-pressed', String(state.selected));
      choose.setAttribute('aria-label', `${part.title}, ${state.selected ? 'selected' : 'not selected'}`);
      if (part.image && safeImage(part.image)) {
        const image = el('img', 'ttb__part-photo'); image.src = safeImage(part.image); image.alt = ''; image.width = 56; image.height = 56; image.loading = 'lazy'; choose.append(image);
      }
      const title = el('span', 'ttb__part-title'); title.append(el('strong', '', part.title), el('small', '', part.kind === 'kit' ? 'Complete kit' : 'Individual piece'));
      const cost = el('span', 'ttb__part-cost'); cost.append(el('strong', '', this.money(price)), el('small', '', !part.product ? 'Planned price' : !variant?.available ? 'Unavailable' : 'Current price'));
      const check = el('span', 'ttb__check', state.selected ? '✓' : '+'); check.setAttribute('aria-hidden', 'true');
      choose.append(title, cost, check); card.append(choose);
      if (!state.selected) return card;
      const body = el('div', 'ttb__part-body');
      if (part.note) body.append(el('p', 'ttb__part-note', part.note));
      const id = `ttb-${this.config.sectionId}-${part.key}`;
      const options = part.product?.variants || part.draftOptions;
      if (options.length > 1) {
        const label = el('label', 'ttb__field-label', part.product ? 'Configuration' : 'Planned configuration'); label.htmlFor = `${id}-options`;
        const select = el('select', 'ttb__select'); select.id = label.htmlFor;
        select.dataset.partControl = part.product ? 'variant' : 'draft'; select.dataset.key = part.key; select.dataset.focus = `options:${part.key}`;
        for (const option of options) {
          const node = el('option', '', part.product ? `${option.options.join(' / ')}${!option.available ? ' (unavailable)' : ''}` : option);
          node.value = part.product ? option.id : option;
          node.selected = String(node.value) === String(part.product ? state.variantId : state.draftOption); select.append(node);
        }
        body.append(label, select);
      } else {
        const label = part.product ? variant ? optionLabel(variant.options) : 'Configuration unavailable' : state.draftOption;
        if (label) body.append(el('p', 'ttb__option-label', label));
      }
      const meta = el('div', 'ttb__part-meta');
      const identity = el('div', 'ttb__identity');
      identity.append(el('strong', '', clean(variant?.sku) ? `SKU ${variant.sku}` : 'SKU pending'), el('span', '', !part.product ? 'Individual product needs setup' : !variant?.available ? 'Not available to order' : 'Available to order'));
      const qty = el('div', 'ttb__quantity');
      const minus = button('', '−', 'minus', part.key); minus.setAttribute('aria-label', `Decrease quantity of ${part.title}`);
      const plus = button('', '+', 'plus', part.key); plus.setAttribute('aria-label', `Increase quantity of ${part.title}`);
      const ruleStep = Number(variant?.increment) || 1;
      minus.disabled = quantity(state.quantity - ruleStep, variant) === state.quantity;
      plus.disabled = quantity(state.quantity + ruleStep, variant) === state.quantity;
      const input = el('input', ''); input.type = 'number'; input.inputMode = 'numeric'; input.value = state.quantity; input.min = variant?.min || 1; input.step = ruleStep;
      if (Number(variant?.max) > 0) input.max = variant.max;
      input.setAttribute('aria-label', `Quantity of ${part.title}`); input.dataset.partControl = 'quantity'; input.dataset.key = part.key; input.dataset.focus = `quantity:${part.key}`;
      qty.append(minus, input, plus); meta.append(identity, qty); body.append(meta); card.append(body); return card;
    }
    renderSummary() {
      const m = this.model;
      const offer = this.node('offer'); offer.replaceChildren();
      offer.hidden = this.config.wholesale || this.config.currency !== 'USD';
      offer.append(el('p', 'ttb__eyebrow', this.config.offerMode === 'verified' ? 'FRONT + REAR OFFER' : 'FRONT + REAR PREVIEW'));
      if (this.config.offerMode === 'disabled') offer.append(el('strong', '', 'Your selected pieces'), el('p', '', 'Any applicable discounts are confirmed in your cart.'));
      else if (!m.offerSupported) offer.append(el('strong', '', 'Rear offer preview is available in retail USD only.'), el('p', '', 'Wholesale and other currencies need a separately approved pricing rule.'));
      else {
        offer.append(el('strong', '', m.unlocked ? this.config.offerMode === 'verified' ? 'Rear-kit offer estimated.' : 'Rear-kit preview unlocked.' : `${this.money(Math.max(0, m.threshold - m.front))} to unlock the rear offer.`));
        const progress = el('progress', 'ttb__progress'); progress.max = m.threshold; progress.value = Math.min(m.front, m.threshold);
        progress.setAttribute('aria-label', 'Qualifying front-parts subtotal'); offer.append(progress);
        offer.append(el('p', '', `${this.money(m.front)} / ${this.money(m.threshold)} in qualifying front pieces. ${this.money(this.config.rearDiscount || 12400)} off one eligible RAW or Low Gloss rear kit. Cart and checkout confirm eligibility.`));
        if (m.unlocked && !m.rows.some(r => r.part.rearOffer)) {
          const rearView = this.config.parts.find(p => p.rearOffer)?.view;
          if (rearView) offer.append(button('ttb__text-button', 'Explore the rear kit →', 'view', rearView));
        }
      }
      offer.classList.toggle('is-unlocked', m.unlocked);
      const items = this.node('summary-items'); items.replaceChildren();
      if (!m.rows.length) items.append(el('p', 'ttb__empty', 'Start with any trim piece. Your layout and price preview will appear here.'));
      for (const row of m.rows) {
        const item = el('div', 'ttb__summary-item'); const info = el('div', '');
        info.append(el('strong', '', `${row.quantity} × ${row.part.title}`), el('small', '', clean(row.variant?.sku) || 'SKU pending'));
        if (row.unavailable) info.append(el('small', 'ttb__warning', 'Selected variant unavailable'));
        if (row.part.rearOffer && m.unlocked && !row.discount) info.append(el('small', '', 'This rear option keeps its regular price.'));
        const cost = el('div', 'ttb__summary-cost'); cost.append(el('strong', '', this.money(row.total === null ? null : row.total - row.discount)));
        cost.append(button('ttb__text-button', 'Remove', 'remove', row.part.key)); item.append(info, cost); items.append(item);
      }
      const totals = this.node('totals'); totals.replaceChildren();
      if (m.saving) { const saving = el('p', 'ttb__saving'); saving.append(el('span', '', this.config.offerMode === 'verified' ? 'Estimated rear savings' : 'Preview rear savings'), el('strong', '', `−${this.money(m.saving)}`)); totals.append(saving); }
      const total = el('p', 'ttb__total'); total.append(el('span', '', m.incomplete ? 'Known-price subtotal' : 'Estimated build total'), el('strong', '', this.money(m.total))); totals.append(total);
      if (m.incomplete) totals.append(el('small', 'ttb__warning', 'Some prices are pending and excluded. This is not a complete total.'));
      if (m.pending) totals.append(el('small', '', 'Includes parts awaiting individual product/SKU setup.'));
      const kitBox = this.node('kit-advice');
      if (kitBox) { kitBox.replaceChildren(); const advice = this.kitCallout(); kitBox.hidden = !advice; if (advice) kitBox.append(advice); }
    }
    kitCallout() {
      const advice = kitAdvice(this.config, this.model);
      if (!advice) return null;
      const box = el('section', 'ttb__kit-advice');
      box.setAttribute('aria-label', 'Complete kit comparison');
      box.append(el('p', 'ttb__eyebrow', 'COMPLETE FRONT SET SELECTED'));
      box.append(el('h3', '', advice.saving ? `Save ${this.money(advice.saving)} with the complete front kit.` : 'Compare the complete kit before you add.'));
      box.append(el('p', '', advice.rear
        ? 'You have selected all six front trim pieces and a rear kit. Check the packaged front + rear bundle before buying these pieces separately.'
        : 'You have selected all six front trim pieces. The packaged kit may be the better value for your build.'));
      const links = el('div', 'ttb__kit-links');
      const addLink = (url, label, price, exact, variant) => {
        if (!url) return;
        const link = el('a', 'ttb__secondary', `${label}${price !== null ? ` — ${exact ? '' : 'from '}${this.money(price)}` : ''} ↗`);
        link.href = url + (variant ? `?variant=${encodeURIComponent(variant)}` : '');
        link.target = '_blank'; link.rel = 'noopener'; links.append(link);
      };
      if (advice.bundleUrl) addLink(advice.bundleUrl, 'Compare front + rear bundles', advice.bundlePrice, false);
      addLink(advice.frontUrl, 'Compare the front kit', advice.frontPrice, advice.exact, advice.frontVariant);
      box.append(links, el('small', '', 'Opens in a new tab. Review window, finish and slider fit before ordering. Your selected pieces stay here.' +
        (advice.rear ? ' Kit pricing replaces the individual-pieces offer; the rear discount may change.' : ' Prices exclude shipping and tax.')));
      return box;
    }
    review() {
      if (!this.model.rows.length) return;
      const content = this.node('review-content'); content.replaceChildren();
      const advice = this.kitCallout(); if (advice) content.append(advice);
      for (const row of this.model.rows) {
        const item = el('div', 'ttb__review-item');
        item.append(el('strong', '', `${row.part.number}. ${row.part.title} × ${row.quantity}`));
        if (row.option && row.option !== 'Default Title') item.append(el('p', '', row.option));
        item.append(el('p', '', `${clean(row.variant?.sku) ? `SKU ${row.variant.sku}` : 'SKU pending'} · ${this.money(row.total === null ? null : row.total - row.discount)}${row.discount ? ' with rear offer' : ''}`));
        if (row.unavailable) item.append(el('small', '', 'Currently unavailable'));
        content.append(item);
      }
      content.append(el('p', 'ttb__review-total', `${this.model.incomplete ? 'Known-price subtotal' : 'Estimated total'}: ${this.money(this.model.total)}`));
      this.updateCartControls();
      if (!this.node('dialog').open) this.node('dialog').showModal();
      this.refreshCartState();
    }
    purchaseBlock() {
      if (!this.config.allowCart || !['disabled', 'verified'].includes(this.config.offerMode)) return 'Purchasing is not enabled for this development preview.';
      if (this.config.wholesale || this.config.currency !== 'USD') return 'Purchasing here is awaiting approval for your customer or currency context. Please use the existing product pages.';
      if (!this.model.rows.length || this.model.pending || this.model.incomplete || this.model.rows.some(r => r.unavailable)) return 'Choose available pieces with confirmed prices and SKUs before adding to cart.';
      return '';
    }
    updateCartControls() {
      const add = this.node('add');
      if (!add) return;
      const locked = this.attempt && this.attempt.state !== 'rejected';
      add.disabled = this.cartBusy || this.cartChecking || !!locked || !!this.purchaseBlock();
      add.textContent = this.cartBusy ? 'Checking your build…' : this.cartChecking ? 'Checking your cart…' : 'Add selected pieces to cart';
      this.node('cart-link').hidden = !this.attempt;
      this.node('new-build').hidden = this.attempt?.state !== 'complete';
      this.node('new-build').disabled = !!this.cartChecking;
      this.node('review-note').textContent = 'Review your selected pieces, fit and quantities. Your cart may contain separate selections.';
      if (!this.cartBusy) this.node('cart-status').textContent = this.cartChecking ? 'Checking whether your previous pieces are still in the cart…' : locked
        ? this.attempt.state === 'complete' ? this.cartNotice || 'These pieces were added previously. View your cart to check quantities and final discounts.' : 'A previous cart request needs review. Open your cart before making another purchase. This build will not be submitted again.'
        : this.purchaseBlock() || this.cartNotice || '';
      this.node('dialog').setAttribute('aria-busy', String(!!(this.cartBusy || this.cartChecking)));
    }
    async refreshCartState() {
      if (this.cartBusy || this.cartChecking || this.attempt?.state !== 'complete') return;
      const previous = this.attempt;
      this.cartChecking = true; this.updateCartControls();
      try {
        const result = await this.transport.inspect(previous);
        if (this.attempt !== previous) return;
        if (result.cart?.currency !== this.config.currency) throw new Error('Cart context changed');
        if (result.state === 'absent') {
          // A confirmed successful add was later removed. Unlock only an explicit new Add click.
          // An uncertain or interrupted request is never cleared by an absent read.
          sessionStorage.removeItem(this.journalKey);
          this.attempt = null;
          this.cartNotice = 'Your previous pieces are no longer in the cart. Your selections are saved; you can add them again.';
        } else if (result.state === 'complete') this.cartNotice = 'These pieces are in your cart. View cart to check quantities and final discounts.';
        else if (result.state === 'partial') this.cartNotice = 'Some of these pieces are still in your cart, with changes. Review your cart before adding another build.';
        else throw new Error('Cart unavailable');
      } catch (_) { this.cartNotice = 'Could not confirm the current cart. Open View cart to check; this build will not be added again automatically.'; }
      finally { this.cartChecking = false; this.updateCartControls(); }
    }
    quoteSignature(config) {
      const model = calculate(config, this.selections);
      return JSON.stringify({ currency: config.currency, wholesale: config.wholesale, allowCart: config.allowCart, offerMode: config.offerMode,
        threshold: config.threshold, rearPrice: config.rearPrice, rearDiscount: config.rearDiscount, rearSkus: config.rearSkus, rearVariantIds: config.rearVariantIds,
        rows: model.rows.map(r => [r.part.key, r.part.product?.id, r.variant?.id, r.variant?.sku, r.quantity, r.price, r.unavailable, r.variant?.min, r.variant?.max, r.variant?.increment]) });
    }
    async freshConfig() {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.config.sectionId);
      url.searchParams.set('_ttb', Date.now());
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Could not check current prices. Please try again.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = html.querySelector('[data-ttb-config]');
        if (!node) throw new Error('Could not validate this build. Reload the page before trying again.');
        const config = JSON.parse(node.textContent);
        if (config.sectionId !== this.config.sectionId || !Array.isArray(config.parts) || config.parts.length !== this.config.parts.length || config.parts.some(p => !this.selections[p.key])) throw new Error('The available pieces changed. Reload the page to review.');
        return config;
      } finally { clearTimeout(timer); }
    }
    async addToCart() {
      if (this.cartBusy || this.cartChecking || this.purchaseBlock() || (this.attempt && this.attempt.state !== 'rejected')) return;
      this.cartNotice = '';
      this.cartBusy = true; const run = this.cartRun = (this.cartRun || 0) + 1; this.updateCartControls();
      let message = '';
      try {
        const fresh = await this.freshConfig();
        if (this.quoteSignature(fresh) !== this.quoteSignature(this.config)) {
          this.config = fresh;
          for (const part of fresh.parts) part.draftOptions = (part.draftOptions || []).map(clean).filter(Boolean);
          this.render(); this.review();
          throw new Error('Prices, availability or purchase rules changed. Review the updated build before adding it.');
        }
        const cartResponse = await this.transport.request('cart.js');
        if (!cartResponse.ok) throw new Error('Could not check your current cart. Please try again.');
        const cart = await cartResponse.json();
        if (cart.currency !== this.config.currency || !Array.isArray(cart.items)) throw new Error('Your cart context changed. Reload this page.');
        for (const row of this.model.rows) {
          const inCart = cart.items.filter(i => String(i.variant_id) === String(row.variant.id)).reduce((n, i) => n + i.quantity, 0);
          const selected = this.model.rows.filter(r => String(r.variant.id) === String(row.variant.id)).reduce((n, r) => n + r.quantity, 0);
          if (Number(row.variant.max) > 0 && inCart + selected > Number(row.variant.max)) throw new Error(`Your cart already includes ${row.part.title}. Review its quantity limit before adding more.`);
        }
        if (run !== this.cartRun) throw new Error('The page was restored. Review your build before trying again.');
        const token = crypto.randomUUID();
        const attempt = { token, items: window.TidyTrimCart.buildItems(this.model, token) };
        const drawer = document.querySelector('mini-cart');
        let sectionIds = [];
        try { sectionIds = (drawer?.getSectionsToRender() || []).map(s => s.section).filter(Boolean).slice(0, 5); } catch (_) { /* Cart page remains available. */ }
        const result = await this.transport.add(attempt, sectionIds, window.location.pathname + window.location.search);
        if (result.state === 'complete') {
          message = 'Your pieces were added. View your cart to confirm quantities, prices and any rear discount.';
          // Failure to refresh the drawer is never an add failure and must never replay the POST.
          try {
            if (!drawer || !sectionIds.length || sectionIds.some(id => typeof result.payload?.sections?.[id] !== 'string')) throw new Error('Drawer unavailable');
            drawer.renderContents(result.payload);
            this.node('dialog').close();
            drawer.open(this.node('review'));
          } catch (_) { message += ' The cart preview could not refresh. Open View cart for the current contents.'; }
        } else if (result.state === 'rejected') message = result.message || 'Shopify could not add these pieces. Your build is saved. Review availability and quantities before trying again.';
        else message = 'Your cart request may have added some or all pieces. Open View cart to check. We will not repeat the request automatically.';
      } catch (error) { message = error.message || 'Could not validate your build. Your selections are saved.'; }
      finally {
        this.cartBusy = false; this.updateCartControls();
        this.node('cart-status').textContent = message; this.announce(message);
      }
    }

  }
  customElements.define('tidy-trim-builder', TidyTrimBuilder);
})();
